import utils from "./utils";

const DEFAULT_ROW_KEYS = [
  "Table",
  "table",
  "Rows",
  "rows",
  "AppointmentList",
  "appointmentList",
  "SuccessStory",
  "successStory",
  "VolunteerList",
  "volunteerList",
  "Data",
  "data",
];

function isRowArray(value) {
  return Array.isArray(value) && value.length > 0 && typeof value[0] === "object";
}

/**
 * Extract the first meaningful row array from a legacy API response envelope.
 */
export function rowsFromResponse(response, preferredKeys = []) {
  if (!response) return [];
  const data = response.data ?? response.Data ?? response;
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];

  const keys = [...preferredKeys, ...DEFAULT_ROW_KEYS];
  for (const key of keys) {
    const value = data[key];
    if (isRowArray(value)) return value;
  }

  if (Array.isArray(data.Tables)) {
    for (const table of data.Tables) {
      const rows = table?.Rows || table?.rows;
      if (isRowArray(rows)) return rows;
    }
  }

  for (const value of Object.values(data)) {
    if (isRowArray(value)) return value;
  }

  return [];
}

/**
 * Return all named tables from a DataSet-style payload.
 */
export function tablesFromResponse(response) {
  const data = response?.data ?? response?.Data ?? response;
  if (!data || typeof data !== "object") return [];

  if (Array.isArray(data.Tables)) {
    return data.Tables.map((table, index) => ({
      name: table?.TableName || table?.tableName || `Table${index}`,
      rows: table?.Rows || table?.rows || [],
    }));
  }

  const tables = [];
  for (const [name, value] of Object.entries(data)) {
    if (isRowArray(value)) {
      tables.push({ name, rows: value });
    }
  }
  return tables;
}

/**
 * Ignore stale async responses when filters change quickly.
 */
export function createStaleRequestGuard() {
  let token = 0;
  return {
    next() {
      token += 1;
      return token;
    },
    isCurrent(requestToken) {
      return requestToken === token;
    },
    invalidate() {
      token += 1;
    },
  };
}

export function assertApiSuccess(response, fallbackMessage = "Request failed.") {
  if (!utils.isApiSuccess(response)) {
    throw new Error(utils.getApiResponseMessage(response, fallbackMessage));
  }
  return response;
}
